import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { GitCompare } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { fmtDate, fmtMoney, PO_STATUS, PR_STATUS, QUOTE_STATUS } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, PageHeader, ReasonsList } from "../../components/common";
import { ApprovalCard } from "../../components/panels";
import { FinActions, Money } from "../../components/finance";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export default function SolicitudDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { map } = useUsers();
  const [busy, setBusy] = useState(false);
  const { data, refetch } = useQuery({
    queryKey: ["fin-pr", id],
    queryFn: async () => (await api.get(`/finanzas/purchase-requests/${id}`)).data,
  });
  if (!data) return null;
  const { purchase_request: pr, quotes, comparison, purchase_order, approvals, exceptions, actions, requirements } = data;

  const act = async (action, payload) => {
    setBusy(true);
    try {
      await api.post(`/finanzas/purchase-requests/${pr.id}/action`, { action, ...payload });
      toast.success("Acción ejecutada y registrada");
      refetch();
      qc.invalidateQueries({ queryKey: ["fin-prs"] });
    } finally { setBusy(false); }
  };

  const budgetCheck = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finanzas/purchase-requests/${pr.id}/budget-check`);
      toast.success(`Disponible tras la solicitud: ${fmtMoney(r.data.available_after)}`);
      refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const buildComparison = async () => {
    setBusy(true);
    try {
      const valid = quotes.filter((q) => q.status === "validada").map((q) => q.id);
      await api.post("/finanzas/comparisons", { purchase_request_id: pr.id, quote_ids: valid });
      toast.success("Comparación creada con recomendación IA (pendiente de decisión humana)");
      refetch();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="solicitud-detail">
      <PageHeader
        title={pr.description}
        subtitle={`${pr.pr_number} · ${pr.area} · Solicitante: ${userName(map, pr.requester_id)} · Necesaria: ${fmtDate(pr.needed_by)}`}
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Chip map={PR_STATUS} value={pr.status} testid="pr-detail-status" />
        {pr.budget_ok ? <span className="ts-chip ts-chip-success">Presupuesto validado</span> : <span className="ts-chip ts-chip-warning">Presupuesto sin validar</span>}
        {pr.purchase_order_id && <Link to={`/finanzas/ordenes/${pr.purchase_order_id}`} className="ts-chip ts-chip-info" data-testid="pr-po-link">Ver orden →</Link>}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="ts-card p-5" data-testid="pr-summary">
            <div className="grid gap-3 sm:grid-cols-3">
              <div><p className="ts-caption">Importe estimado</p><Money value={pr.estimated_amount} className="text-xl font-bold text-slate-900" /></div>
              <div><p className="ts-caption">Partida</p><p className="font-mono text-sm">{pr.budget_reference || "—"}</p></div>
              <div><p className="ts-caption">Proveedor propuesto</p><p className="text-sm">{pr.supplier_name || "—"}</p></div>
            </div>
            <p className="mt-3 text-sm text-slate-600">{pr.justification}</p>
            {pr.budget_check && (
              <div className="mt-3 rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="pr-budget-check">
                Presupuesto {pr.budget_check.budget_reference}: {fmtMoney(pr.budget_check.budget)} · comprometido {fmtMoney(pr.budget_check.committed)} · reservado {fmtMoney(pr.budget_check.reserved)} →
                <strong className={pr.budget_check.over ? "text-red-600" : "text-emerald-700"}> disponible tras la solicitud: {fmtMoney(pr.budget_check.available_after)}</strong>
              </div>
            )}
          </div>

          {requirements?.length > 0 && (
            <div className="ts-card p-5" data-testid="pr-requirements">
              <h3 className="mb-3 font-display text-sm font-semibold">Requisitos pendientes para solicitar aprobación</h3>
              <ReasonsList reasons={requirements} testid="pr-requirements-list" />
            </div>
          )}

          <div className="ts-card overflow-hidden" data-testid="pr-quotes">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <h3 className="font-display text-sm font-semibold">Ofertas ({quotes.length})</h3>
              {!comparison && quotes.filter((q) => q.status === "validada").length >= 2 && (
                <Button size="sm" variant="outline" onClick={buildComparison} disabled={busy} data-testid="build-comparison-button">
                  <GitCompare className="mr-1 h-3.5 w-3.5" /> Crear comparación
                </Button>
              )}
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>Oferta</TableHead><TableHead>Proveedor</TableHead><TableHead>Plazo</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
              <TableBody>
                {quotes.length === 0 && <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-slate-500">Sin ofertas relacionadas. Regístralas desde Ofertas.</TableCell></TableRow>}
                {quotes.map((q) => (
                  <TableRow key={q.id} data-testid={`pr-quote-${q.id}`}>
                    <TableCell className="font-mono text-xs">{q.quote_number}</TableCell>
                    <TableCell className="text-sm">{q.supplier_name}</TableCell>
                    <TableCell className="text-xs">{q.delivery_weeks ? `${q.delivery_weeks} semanas` : "—"}</TableCell>
                    <TableCell><Chip map={QUOTE_STATUS} value={q.status} /></TableCell>
                    <TableCell className="text-right"><Money value={q.total} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {comparison && (
            <div className="ts-card p-5" data-testid="pr-comparison">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-display text-sm font-semibold">Comparación {comparison.comparison_number}</h3>
                <Link to={`/finanzas/comparaciones/${comparison.id}`} data-testid="open-comparison-link">
                  <Button size="sm" variant="outline"><GitCompare className="mr-1 h-3.5 w-3.5" /> Abrir comparación y seleccionar</Button>
                </Link>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Recomendación IA: <strong>{comparison.recommendation?.supplier}</strong> — {comparison.recommendation?.justification}
              </p>
              {comparison.selected_quote_id
                ? <p className="mt-1 text-sm text-emerald-700">Seleccionada por {userName(map, comparison.selected_by)} (decisión humana).</p>
                : <p className="mt-1 text-xs text-amber-700">Pendiente de selección humana: la IA recomienda, pero no decide.</p>}
            </div>
          )}

          {purchase_order && (
            <div className="ts-card flex items-center justify-between p-5" data-testid="pr-order">
              <div>
                <p className="font-mono text-xs text-slate-400">{purchase_order.po_number}</p>
                <p className="text-sm font-medium">{purchase_order.supplier_name} — <Money value={purchase_order.total} /></p>
              </div>
              <Chip map={PO_STATUS} value={purchase_order.status} />
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="ts-card p-5" data-testid="pr-actions-card">
            <h3 className="mb-3 font-display text-sm font-semibold">Acciones controladas</h3>
            <FinActions actions={actions} onAct={act} testid="pr-action" />
            {!pr.budget_ok && (
              <Button size="sm" variant="outline" className="mt-2" onClick={budgetCheck} disabled={busy} data-testid="pr-budget-check-button">
                Ejecutar validación presupuestaria
              </Button>
            )}
          </div>
          {exceptions.length > 0 && (
            <div className="ts-card p-5" data-testid="pr-exceptions">
              <h3 className="mb-2 font-display text-sm font-semibold">Excepciones</h3>
              {exceptions.map((e) => (
                <div key={e.id} className="mb-2 rounded-sm border border-red-200 bg-red-50 p-2 text-xs" data-testid={`pr-exc-${e.id}`}>
                  <span className="font-semibold text-red-800">{e.type}</span> — {e.description}
                  <p className="mt-1 text-red-600">Estado: {e.status}</p>
                </div>
              ))}
              <Link to="/finanzas/excepciones" className="text-xs font-medium text-blue-700 hover:underline">Gestionar en Excepciones →</Link>
            </div>
          )}
          <div className="space-y-3" data-testid="pr-approvals">
            {approvals.map((a) => <ApprovalCard key={a.id} appr={a} me={null} onChanged={refetch} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
